// VerifyEmailWall.jsx — the app, withheld until an email signup confirms its address.
//
// INSTEAD OF THE APP, NOT OVER IT. Rendered in App.jsx's page switch, in the same position as
// the maintenance notice, so nothing behind it mounts or fires calls. A dismissible modal was
// the previous shape and it was the wrong one: the product sat readable underneath, and
// "unverified" only meant a smaller allowance rather than no access.
//
// Firebase signs you in as a SIDE EFFECT of creating the credential — there is no
// "registered but not signed in" state to hold someone in — so being signed in is not the
// same as being let in, and this is what makes that distinction real.
//
// House style: no spinners, no gradients, no blur.
import React, { useCallback, useEffect, useState } from "react";
import { MailCheck } from "lucide-react";
import { getAuth } from "firebase/auth";
import { resendVerification, signOutUser } from "../auth";
import { useT } from "../i18n/useT";

const VerifyEmailWall = ({ user }) => {
  const t = useT();
  const [busy, setBusy] = useState(null);   // "check" | "resend" | null
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // RELOAD, then read. emailVerified is a property of the cached user object, so confirming in
  // another tab does not change it here — without the reload this would always report "not
  // confirmed yet" at the exact moment it should be letting someone in.
  const checkVerified = useCallback(async () => {
    try {
      await user.reload();
      if (getAuth().currentUser?.emailVerified) {
        // App.jsx re-renders off the auth listener, but a reload is the simplest way to
        // re-enter with every downstream query refetched rather than half-populated here.
        window.location.reload();
        return true;
      }
    } catch { /* offline or token hiccup — the next poll tries again */ }
    return false;
  }, [user]);

  // POLLING IS THE PRIMARY PATH. Asking someone to come back and press a button is the unusual
  // shape; nearly every product that does this simply notices. The button below stays as a
  // fallback for when polling cannot run (blocked network, throttled background tab), which is
  // also why it is a text link now rather than the main action.
  //
  // Only while the tab is VISIBLE, and immediately on becoming visible again — returning to
  // this tab after clicking the link in another one is precisely when the answer changes, and
  // a hidden tab polling every 3s forever is a request-per-3s that helps nobody.
  useEffect(() => {
    let stop = false;
    let timer = null;

    const tick = async () => {
      if (stop || document.visibilityState !== "visible") return;
      const done = await checkVerified();
      if (!done && !stop) timer = setTimeout(tick, 3000);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timer);
        tick();
      }
    };

    tick();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stop = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkVerified]);

  const recheck = async () => {
    setBusy("check");
    setError("");
    setNotice("");
    if (!(await checkVerified())) setError(t("verify_wall_still_unverified"));
    setBusy(null);
  };

  const resend = async () => {
    setBusy("resend");
    setError("");
    setNotice("");
    const result = await resendVerification();
    setNotice(result?.success ? t("verify_wall_resent") : "");
    if (!result?.success) setError(t("generic_error"));
    setBusy(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
         style={{ background: "#0a0a0f" }}>
      <div className="flex items-center gap-2 mb-8">
        <span className="text-[40px] font-extrabold text-[#635BFF] tracking-tight">Magi</span>
        <img src="/magi-icon.svg" alt="" className="w-12 h-12" />
      </div>

      <div className="rounded-2xl shadow-2xl max-w-sm w-full p-7 text-center"
           style={{ background: "#131313", border: "1px solid #232326" }}>
        <div className="w-14 h-14 rounded-full bg-[#123322] flex items-center justify-center mx-auto mb-5">
          <MailCheck className="w-7 h-7 text-green-400" />
        </div>

        <h1 className="text-xl font-bold text-white mb-2">{t("verify_wall_title")}</h1>
        <p className="text-sm text-gray-400 leading-relaxed mb-6">
          {t("verify_wall_desc", { email: user?.email || "" })}
        </p>

        {notice && (
          <div className="mb-4 px-3 py-2 rounded-lg text-sm bg-[#123322] text-green-300">{notice}</div>
        )}
        {error && (
          <div className="mb-4 px-3 py-2 rounded-lg text-sm bg-[#3A1A1A] text-red-300" role="alert">
            {error}
          </div>
        )}

        {/* Resending is the only thing a person here can usefully DO — the confirming itself
            happens in their inbox, and the poll above notices it without being asked. */}
        <button
          onClick={resend}
          disabled={busy !== null}
          className="w-full py-3 px-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors disabled:opacity-60"
        >
          {t("verify_wall_resend")}
        </button>

        <button
          onClick={recheck}
          disabled={busy !== null}
          className="mt-4 text-sm text-gray-400 hover:text-gray-200 transition-colors"
        >
          {busy === "check" ? t("verify_wall_checking") : t("verify_wall_check")}
        </button>

        {/* A way OUT is load-bearing. Without it, anyone who mistyped their address at signup
            is sealed behind a wall addressed to an inbox they cannot open. */}
        <button
          // Same destination as the app's own sign-out, and a HARD load rather than a client
          // nav: this component is the only thing mounted, so there is no app shell to hand
          // the transition to, and a full load guarantees no half-signed-out frame.
          onClick={async () => { await signOutUser(); window.location.assign("/landing"); }}
          className="mt-2 block w-full text-sm text-gray-400 hover:text-gray-200 transition-colors"
        >
          {t("verify_wall_wrong_account")}
        </button>
      </div>
    </div>
  );
};

export default VerifyEmailWall;
